import { cloneDeep } from 'lodash';
import { lzFKebabToWordCase, lzFSingularize } from '@lz/services/String';
import LzMBase from '../commons/Base';
import { LzTOpenObject } from '@lz/typings';
import LzMCrudField, { LzNCrudField, LzTCrudFieldGroup } from './Field';
import LzMCrudListingColumn, { LzTCrudListingColumnGroup } from './ListingColumn';
import LzMCrudInput, { LzTCrudInputGroup } from './Input';
import LzMCrudListingActionIcon, { LzTCrudListingActionIconGroup } from './ListingActionIcon';
import LzMCrudListGroupActionIcon, { LzTCrudListGroupActionIconGroup } from './ListingGroupActionIcon';

export interface LzNCrud {
    ident: string;
    baseRoute?: string;

    /**
     * Rights path of this entity separated by dots(.) and in snake_case, (e.g. system.users, params.accounting, etc.).
     * If not provided, then rights will not be checked.
     */
    rightsPath?: string;

    labels?: string;
    label?: string;
    className?: string;
    isTreeBased?: boolean;
    treeNodeLabel?: (record: LzMBase) => string;
    parentName?: (record: LzMBase) => string;
    fields?: LzTCrudFieldGroup;
    listingColumns?: LzTCrudListingColumnGroup;
    addFormInputs?: LzTCrudInputGroup;
    viewFormFields?: LzTCrudInputGroup;
    editFormInputs?: LzTCrudInputGroup;
    showExport?: boolean;
    showImport?: boolean;
    importGetInitParams?: () => any;
    importGetParamsInput?: (
        data: LzTOpenObject,
        handleInputChanage: (name: string, value: any) => void
    ) => React.ReactNode;
    showAdd?: boolean;
    routeAfterAdd?: string | undefined;
    getRouteAfterAdd?: (rec: any) => string;
    getRouteAfterAddParams?: (rec: any) => LzTOpenObject;
    resetAfterAdd?: boolean;
    routeOnAddClose?: string;
    getRouteOnAddClose?: (rec: any, locState: any, qsVars: any) => string;
    showTreeAdd?: boolean;
    showView?: boolean;
    showEdit?: boolean;
    routeOnEditClose?: string;
    getRouteOnEditClose?: (rec: any, locState: any, qsVars: any) => string;
    showStatus?: boolean;
    showDelete?: boolean;
    showDeletedList?: boolean;
    allowDeletedRestore?: boolean;
    allowDeletedPurge?: boolean;
    listingActionIcons?: LzTCrudListingActionIconGroup;
    noGroupActions?: boolean;
    showStatusGroupAction?: boolean;
    showDeleteGroupAction?: boolean;
    listingGroupActionIcons?: LzTCrudListGroupActionIconGroup;
    routeParams?: LzTOpenObject;
    getRouteParams?: (locState: any) => LzTOpenObject;

    // Parent field that is used to show a dropdown on top or a static name of the selected parent and show only its children
    parentFieldKey?: string;
    parentFieldRoute?: string;
    parentFieldShowFilterOnListing?: boolean;
}

class LzMCrud implements LzNCrud {
    ident: string;
    baseRoute: string;
    rightsPath: string;
    labels: string;
    label: string;
    className?: string;
    isTreeBased: boolean;
    treeNodeLabel: (record: LzMBase) => string;
    parentName: (record: LzMBase) => string;
    fields: LzTCrudFieldGroup;
    listingColumns: LzTCrudListingColumnGroup;
    addFormInputs: LzTCrudInputGroup;
    viewFormFields: LzTCrudInputGroup;
    editFormInputs: LzTCrudInputGroup;
    showExport: boolean;
    showImport: boolean;
    importGetInitParams?: () => any;
    importGetParamsInput?: (data: LzTOpenObject, handleInputChanage: (name: string, value: any) => void) => React.ReactNode;
    showAdd: boolean;
    showTreeAdd: boolean;
    routeAfterAdd?: string | undefined;
    getRouteAfterAdd: (rec: any) => string;
    getRouteAfterAddParams?: (rec: any) => LzTOpenObject;
    resetAfterAdd: boolean;
    routeOnAddClose: string;
    getRouteOnAddClose: (rec: any, locState: any, qsVars: any) => string;
    showView: boolean;
    showEdit: boolean;
    routeOnEditClose: string;
    getRouteOnEditClose: (rec: any, locState: any, qsVars: any) => string;
    showStatus: boolean;
    showDelete: boolean;
    showDeletedList: boolean;
    allowDeletedRestore: boolean;
    allowDeletedPurge: boolean;
    listingActionIcons: LzTCrudListingActionIconGroup;
    noGroupActions: boolean;
    showStatusGroupAction: boolean;
    showDeleteGroupAction: boolean;
    listingGroupActionIcons: LzTCrudListGroupActionIconGroup;
    routeParams: LzTOpenObject;
    getRouteParams: (locState: any) => LzTOpenObject;
    parentFieldKey?: string | undefined;
    parentFieldRoute?: string | undefined;
    parentFieldShowFilterOnListing?: boolean | undefined;

    constructor(model?: LzNCrud) {
        this.ident = model?.ident || '';
        this.baseRoute = model?.baseRoute || '/' + this.ident;
        this.rightsPath = model?.rightsPath || '';
        this.labels = model?.labels || lzFKebabToWordCase(this.ident);
        this.label = model?.label || lzFSingularize(this.labels);
        this.className = model?.className;
        this.isTreeBased = !!model?.isTreeBased;
        this.treeNodeLabel = model?.treeNodeLabel ? model?.treeNodeLabel : (record: LzMBase) => record.name || '';
        this.parentName = model?.parentName ? model.parentName : (record: LzMBase) => record.name || '';
        this.fields = LzMCrudField.parseData(model?.fields);
        this.listingColumns = LzMCrudListingColumn.parseData(model?.listingColumns, this.fields);
        this.addFormInputs = LzMCrudInput.parseData(model?.addFormInputs, this.fields);
        this.viewFormFields = LzMCrudInput.parseData(model?.viewFormFields, this.fields, true);
        this.editFormInputs = LzMCrudInput.parseData(model?.editFormInputs, this.fields);
        this.showExport = !!model?.showExport;
        this.showImport = !!model?.showImport;
        this.importGetInitParams = model?.importGetInitParams;
        this.importGetParamsInput = model?.importGetParamsInput || this.dummyImportGetParamsInputCallback;
        this.showAdd = typeof model?.showAdd !== 'undefined' ? model.showAdd : true;
        this.showTreeAdd = typeof model?.showTreeAdd !== 'undefined' ? model.showTreeAdd : true;
        this.routeAfterAdd = model?.routeAfterAdd;
        this.getRouteAfterAdd = model?.getRouteAfterAdd || (() => this.routeAfterAdd || '');
        this.getRouteAfterAddParams = model?.getRouteAfterAddParams;
        this.resetAfterAdd = typeof model?.resetAfterAdd !== 'undefined' ? !!model.resetAfterAdd : true;
        this.routeOnAddClose = model?.routeOnAddClose || this.baseRoute;
        this.getRouteOnAddClose = model?.getRouteOnAddClose || (() => this.routeOnAddClose);
        this.showView = typeof model?.showView !== 'undefined' ? model.showView : true;
        this.showEdit = typeof model?.showEdit !== 'undefined' ? model.showEdit : true;
        this.routeOnEditClose = model?.routeOnEditClose || this.baseRoute;
        this.getRouteOnEditClose = model?.getRouteOnEditClose || (() => this.routeOnEditClose);
        this.showStatus = typeof model?.showStatus !== 'undefined' ? model.showStatus : !!this.fields.is_active;
        this.showDelete = typeof model?.showDelete !== 'undefined' ? model.showDelete : true;
        this.showDeletedList = typeof model?.showDeletedList !== 'undefined' ? model.showDeletedList : true;
        this.allowDeletedRestore = typeof model?.allowDeletedRestore !== 'undefined' ? model.allowDeletedRestore : this.showDeletedList;
        this.allowDeletedPurge = typeof model?.allowDeletedPurge !== 'undefined' ? model.allowDeletedPurge : this.showDeletedList;
        this.listingActionIcons = LzMCrudListingActionIcon.parseData(model?.listingActionIcons, this);
        this.noGroupActions = !!model?.noGroupActions;
        this.showStatusGroupAction = typeof model?.showStatusGroupAction !== 'undefined' ? model.showStatusGroupAction : this.showStatus;
        this.showDeleteGroupAction = typeof model?.showDeleteGroupAction !== 'undefined' ? model.showDeleteGroupAction : this.showDelete;
        this.listingGroupActionIcons = LzMCrudListGroupActionIcon.parseData(model?.listingGroupActionIcons, this);
        this.routeParams = model?.routeParams || {};
        this.getRouteParams = model?.getRouteParams ? model?.getRouteParams : (locState?: any) => ({ ...locState, ...this.routeParams });
        this.parentFieldKey = model?.parentFieldKey;
        this.parentFieldRoute = model?.parentFieldRoute;
        this.parentFieldShowFilterOnListing = !!model?.parentFieldShowFilterOnListing;
    }

    private dummyImportGetParamsInputCallback(
        data: LzTOpenObject,
        handleInputChanage: (name: string, value: any) => void
    ) {
        return undefined;
    }

    get hasGroupActions() {
        return (
            !this.noGroupActions &&
            Object.keys(this.listingGroupActionIcons).length > 0
        );
    }

    get clone() {
        return cloneDeep(this);
    }

    addField(params: LzNCrudAddFieldParams) {
        // Add the actual field if not already existing
        if (typeof this.fields[params.key] === 'undefined') {
            this.fields[params.key] = params.spec;
        }

        // Add the listing column if provided and not already existing
        if (
            typeof params.listingColumn !== 'undefined' &&
            typeof this.listingColumns[params.key] === 'undefined'
        ) {
            this.listingColumns[params.key] = params.listingColumn;
        }

        // Add the add form input if provided and not already existing
        if (
            typeof params.addFormInput !== 'undefined' &&
            typeof this.addFormInputs[params.key] === 'undefined'
        ) {
            this.addFormInputs[params.key] = params.addFormInput;
        }

        // Add the view form field if provided and not already existing
        if (
            typeof params.viewFormField !== 'undefined' &&
            typeof this.viewFormFields[params.key] === 'undefined'
        ) {
            this.viewFormFields[params.key] = params.viewFormField;
        }

        // Add the edit form input if provided and not already existing
        if (
            typeof params.editFormInput !== 'undefined' &&
            typeof this.editFormInputs[params.key] === 'undefined'
        ) {
            this.editFormInputs[params.key] = params.editFormInput;
        }
    }

    deleteField(key: string) {
        // Delete the actual field if exists
        if (typeof this.fields[key] !== 'undefined') {
            delete this.fields[key];
        }

        // Delete the listing column if exists
        if (typeof this.listingColumns[key] !== 'undefined') {
            delete this.listingColumns[key];
        }

        // Delete the add form input if exists
        if (typeof this.addFormInputs[key] !== 'undefined') {
            delete this.addFormInputs[key];
        }

        // Delete the view form field if exists
        if (typeof this.viewFormFields[key] !== 'undefined') {
            delete this.viewFormFields[key];
        }

        // Delete the edit form input if exists
        if (typeof this.editFormInputs[key] !== 'undefined') {
            delete this.editFormInputs[key];
        }
    }

    deleteAddIcon() {
        this.showTreeAdd = false; // because add in listing is only shown for trees
        if (typeof this.listingActionIcons['add'] !== 'undefined') {
            delete this.listingActionIcons['add'];
        }
    }

    deleteViewIcon() {
        this.showView = false;
        if (typeof this.listingActionIcons['view'] !== 'undefined') {
            delete this.listingActionIcons['view'];
        }
    }

    deleteEditIcon() {
        this.showEdit = false;
        if (typeof this.listingActionIcons['edit'] !== 'undefined') {
            delete this.listingActionIcons['edit'];
        }
    }

    deleteStatusIcon() {
        this.showStatus = false;
        if (typeof this.listingActionIcons['status'] !== 'undefined') {
            delete this.listingActionIcons['status'];
        }
    }

    deleteDeleteIcon() {
        this.showDelete = false;
        if (typeof this.listingActionIcons['delete'] !== 'undefined') {
            delete this.listingActionIcons['delete'];
        }
    }

    deleteRightsIcon() {
        if (typeof this.listingActionIcons['rights'] !== 'undefined') {
            delete this.listingActionIcons['rights'];
        }
    }

    setListingActionIconHideOnCondition(
        key: string,
        condition: (record: any) => boolean
    ) {
        this.listingActionIcons[key].hideOnCondition = condition;
    }
}

export interface LzNCrudAddFieldParams {
    key: string;
    spec: LzNCrudField;
    listingColumn?: LzMCrudListingColumn;
    addFormInput?: LzMCrudInput;
    viewFormField?: LzMCrudInput;
    editFormInput?: LzMCrudInput;
}

export default LzMCrud;
